<template>
  <div>
    <header class="flex align-items-center justify-content-between mb-3">
      <div>
        <h2 class="m-0 p-0 font-light text-600">Change Attendance Company</h2>
        <p v-if="attendanceIds.length" class="m-0 pt-1 text-xs text-300">
          Managing {{ attendanceIds.length }} attendance records. Only the company can be changed.
        </p>
      </div>
      <Button
        label="Back"
        icon="pi pi-arrow-left"
        class="surface-300 hover:surface-500"
        :disabled="submitting"
        @click="handleBack"
      />
    </header>

    <div v-if="routeError" class="card">
      <Message severity="error" :closable="false">{{ routeError }}</Message>
    </div>

    <div v-else class="card overflow-x-auto">
      <DataTable
        :value="editableRows"
        dataKey="attendance_id"
        size="small"
        :loading="loading || companiesLoading"
        tableStyle="min-width: 70rem; min-height: 20rem;"
      >
        <Column field="employee_name" header="Employee" class="text-600 w-3">
          <template #body="slotProps">
            <span class="block font-bold">{{ slotProps.data.employee_name }}</span>
            <span class="block text-xs text-300">
              @{{ slotProps.data.employee_id }} · {{ slotProps.data.company_name }}
            </span>
          </template>
        </Column>
        <Column field="shift_type" header="Type" class="text-600 w-1">
          <template #body="slotProps">
            <Tag :value="slotProps.data.shift_type" severity="secondary" />
          </template>
        </Column>
        <Column field="attendance_checkin" header="Date" class="text-600 w-2">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.attendance_checkin) }}
          </template>
        </Column>
        <Column field="attendance_checkin" header="Start" class="text-600 w-1">
          <template #body="slotProps">
            {{ formatTime(slotProps.data.attendance_checkin) }}
          </template>
        </Column>
        <Column field="attendance_checkout" header="End" class="text-600 w-1">
          <template #body="slotProps">
            <span v-if="slotProps.data.attendance_checkout">
              {{ formatTime(slotProps.data.attendance_checkout) }}
            </span>
            <span v-else class="text-300">No checkout</span>
          </template>
        </Column>
        <Column header="Change Company" class="text-600 w-4">
          <template #body="slotProps">
            <div class="flex align-items-center gap-2">
              <Select
                v-model="slotProps.data.selected_company_id"
                :options="companyOptionsFor(slotProps.data)"
                optionLabel="company_name"
                optionValue="company_id"
                filter
                filterPlaceholder="Search companies"
                placeholder="Select company"
                class="flex-1"
                :disabled="submitting"
              />
              <Button
                icon="pi pi-arrow-down"
                class="w-3rem surface-300 hover:surface-500"
                aria-label="Apply company to all rows below"
                v-tooltip.top="'Apply company to all rows below'"
                :disabled="!canApplyCompanyBelow(slotProps.index)"
                @click="applyCompanyBelow(slotProps.index)"
              />
            </div>
          </template>
        </Column>
        <template #loading>
          <div class="text-400 !py-20">Loading attendance records...</div>
        </template>
      </DataTable>

      <Message v-if="saveError" severity="error" :closable="false" class="mt-3">
        {{ saveError }}
      </Message>

      <div class="flex justify-content-end gap-2 mt-3">
        <Button
          label="Cancel"
          class="surface-300 hover:surface-500"
          :disabled="submitting"
          @click="handleBack"
        />
        <Button
          label="Save Companies"
          icon="pi pi-save"
          :loading="submitting"
          :disabled="changedRows.length === 0 || loading || companiesLoading"
          @click="saveCompanies"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Message from 'primevue/message';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import { useAttendanceCompanyBulk } from '@/composables/api/useAttendanceCompanyBulk';
import { useCompaniesStore } from '@/stores/companies';
import {
  parseAttendanceRouteFilters,
  parseAttendanceSortQuery,
} from '@/composables/useAttendanceRouteFilters';

const MAX_ATTENDANCE_IDS = 100;
const route = useRoute();
const router = useRouter();
const toast = useToast();
const companiesStore = useCompaniesStore();
const { companiesList, companiesLoading, companiesError } = storeToRefs(companiesStore);
const {
  attendanceRecords,
  loading,
  submitting,
  error,
  fetchAttendanceRecords,
  updateAttendanceCompanies,
} = useAttendanceCompanyBulk();

const editableRows = ref([]);
const initialized = ref(false);
const saveError = ref(null);

const parsedRoute = computed(() => {
  const rawAttendanceIds = route.params.attendanceIds;
  if (typeof rawAttendanceIds !== 'string' || rawAttendanceIds.trim() === '') {
    return { attendanceIds: [], error: 'No attendance IDs were provided.' };
  }

  const segments = rawAttendanceIds.split(',');
  if (segments.some(segment => !/^\d+$/.test(segment))) {
    return { attendanceIds: [], error: 'Attendance IDs must be positive integers separated by commas.' };
  }

  const attendanceIds = segments.map(Number);
  if (attendanceIds.some(id => !Number.isSafeInteger(id) || id <= 0)) {
    return { attendanceIds: [], error: 'One or more attendance IDs are invalid.' };
  }
  if (new Set(attendanceIds).size !== attendanceIds.length) {
    return { attendanceIds: [], error: 'Attendance IDs must not contain duplicates.' };
  }
  if (attendanceIds.length > MAX_ATTENDANCE_IDS) {
    return {
      attendanceIds: [],
      error: `A maximum of ${MAX_ATTENDANCE_IDS} attendance records can be changed at once.`,
    };
  }

  return { attendanceIds, error: null };
});

const attendanceIds = computed(() => parsedRoute.value.attendanceIds);
const routeError = computed(() => (
  parsedRoute.value.error
  || (initialized.value ? error.value : null)
  || (initialized.value ? companiesError.value : null)
));

const changedRows = computed(() => editableRows.value.filter(row => (
  Number(row.selected_company_id) !== Number(row.company_id)
)));

const safeReturnTo = computed(() => {
  const returnTo = route.query.returnTo;
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return null;
  }

  const resolvedRoute = router.resolve(returnTo);
  if (resolvedRoute.name !== 'ViewAttendanceFiltered') return null;
  const filters = parseAttendanceRouteFilters(resolvedRoute.params);
  const sort = parseAttendanceSortQuery(resolvedRoute.query);
  return filters.valid && sort.valid ? resolvedRoute.fullPath : null;
});

function companyOptionsFor(row) {
  if (companiesList.value.some(company => Number(company.company_id) === Number(row.company_id))) {
    return companiesList.value;
  }
  return [
    { company_id: row.company_id, company_name: row.company_name },
    ...companiesList.value,
  ];
}

function canApplyCompanyBelow(rowIndex) {
  const row = editableRows.value[rowIndex];
  const selectedCompanyId = Number(row?.selected_company_id);
  return rowIndex < editableRows.value.length - 1
    && Number.isSafeInteger(selectedCompanyId)
    && selectedCompanyId > 0
    && !loading.value
    && !companiesLoading.value
    && !submitting.value;
}

function applyCompanyBelow(rowIndex) {
  if (!canApplyCompanyBelow(rowIndex)) return;

  const selectedCompanyId = Number(editableRows.value[rowIndex].selected_company_id);
  editableRows.value = editableRows.value.map((row, index) => (
    index > rowIndex
      ? { ...row, selected_company_id: selectedCompanyId }
      : row
  ));
}

function formatDate(timestamp) {
  const date = new Date(Number(timestamp));
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatTime(timestamp) {
  const date = new Date(Number(timestamp));
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function navigateBackOrFallback(replaceFallback = false) {
  if (safeReturnTo.value) {
    router.replace(safeReturnTo.value);
  } else if (window.history.state?.back) {
    router.back();
  } else if (replaceFallback) {
    router.replace({ name: 'ViewAttendance' });
  } else {
    router.push({ name: 'ViewAttendance' });
  }
}

const handleBack = () => navigateBackOrFallback(false);

async function saveCompanies() {
  if (changedRows.value.length === 0) return;
  saveError.value = null;
  const result = await updateAttendanceCompanies(changedRows.value.map(row => ({
    attendance_id: row.attendance_id,
    company_id: Number(row.selected_company_id),
  })));
  if (!result.success) {
    saveError.value = result.error;
    return;
  }

  toast.add({
    severity: 'success',
    summary: 'Companies updated',
    detail: result.data.message,
    life: 4000,
    group: 'br',
  });
  navigateBackOrFallback(true);
}

onMounted(async () => {
  if (parsedRoute.value.error) {
    initialized.value = true;
    return;
  }

  const [loaded] = await Promise.all([
    fetchAttendanceRecords(attendanceIds.value),
    companiesStore.ensureCompanies().catch(() => null),
  ]);
  if (loaded) {
    editableRows.value = attendanceRecords.value.map(record => ({
      ...record,
      selected_company_id: Number(record.company_id),
    }));
  }
  initialized.value = true;
});
</script>
